import React, { useEffect, useRef, useState } from 'react';
import { downloadPdfData } from '../utils/pdfAccess';

const PDFJS_VERSION = '3.11.174';
let pdfJsPromise = null;

const ensurePdfJsLoaded = async () => {
  if (window.pdfjsLib?.getDocument) return window.pdfjsLib;
  if (pdfJsPromise) return pdfJsPromise;

  pdfJsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-pdfjs-inline-viewer]');
    if (existing) {
      if (window.pdfjsLib?.getDocument || existing.dataset.ready === '1') {
        resolve(window.pdfjsLib);
        return;
      }
      existing.addEventListener('load', () => resolve(window.pdfjsLib), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
    script.async = true;
    script.setAttribute('data-pdfjs-inline-viewer', '1');
    script.onload = () => {
      script.dataset.ready = '1';
      resolve(window.pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  const lib = await pdfJsPromise;
  if (lib?.GlobalWorkerOptions) {
    lib.GlobalWorkerOptions.workerSrc =
      `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
  }
  return lib;
};

function PdfViewer({
  storageUrl,
  title,
  isFullscreen = false,
  onToggleFullscreen,
  showFullscreen = true
}) {
  const canvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);
  const [docReadyToken, setDocReadyToken] = useState(0);

  const canGoPrev = pageNumber > 1 && !loading && !rendering;
  const canGoNext = pageNumber < numPages && !loading && !rendering;

  useEffect(() => {
    let cancelled = false;

    async function loadDocument() {
      setLoading(true);
      setError('');
      setPageNumber(1);
      setNumPages(0);

      try {
        const data = await downloadPdfData(storageUrl);
        if (cancelled) return;

        const pdfjsLib = await ensurePdfJsLoaded();
        if (cancelled || !pdfjsLib?.getDocument) return;

        if (pdfDocRef.current?.destroy) {
          await pdfDocRef.current.destroy();
        }

        const loadingTask = pdfjsLib.getDocument({
          data,
          disableRange: true,
          disableStream: true
        });
        const pdfDoc = await loadingTask.promise;
        if (cancelled) return;

        pdfDocRef.current = pdfDoc;
        setNumPages(pdfDoc.numPages || 0);
        setDocReadyToken((prev) => prev + 1);
      } catch (loadError) {
        console.error('Erro ao carregar PDF no viewer interno:', loadError);
        if (!cancelled) {
          setError(loadError?.message || 'Nao foi possivel carregar este PDF no momento.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [storageUrl, retryToken]);

  useEffect(() => {
    const pdfDoc = pdfDocRef.current;
    if (!pdfDoc || !canvasRef.current) return;

    let cancelled = false;

    async function renderPage() {
      try {
        setRendering(true);
        setError('');

        const safePage = Math.min(Math.max(pageNumber, 1), pdfDoc.numPages || 1);
        if (safePage !== pageNumber) {
          setPageNumber(safePage);
          return;
        }

        const page = await pdfDoc.getPage(safePage);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1.25 });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        renderTaskRef.current = page.render({ canvasContext: context, viewport });
        await renderTaskRef.current.promise;
      } catch (renderError) {
        if (!cancelled) {
          console.error('Erro ao renderizar pagina do PDF:', renderError);
          setError('Falha ao renderizar a pagina do PDF.');
        }
      } finally {
        if (!cancelled) {
          setRendering(false);
        }
      }
    }

    renderPage();

    return () => {
      cancelled = true;
      if (renderTaskRef.current?.cancel) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pageNumber, docReadyToken]);

  useEffect(() => {
    return () => {
      if (pdfDocRef.current?.destroy) {
        pdfDocRef.current.destroy();
      }
    };
  }, []);

  return (
    <div className="pdf-viewer-shell">
      <div className="pdf-controls">
        {showFullscreen && onToggleFullscreen && (
          <button onClick={onToggleFullscreen} className="control-btn" type="button">
            {isFullscreen ? 'Sair do Full Screen' : 'Full Screen'}
          </button>
        )}
        <button
          type="button"
          className="control-btn"
          disabled={!canGoPrev}
          onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
        >
          Anterior
        </button>
        <button
          type="button"
          className="control-btn"
          disabled={!canGoNext}
          onClick={() => setPageNumber((prev) => Math.min(prev + 1, numPages || prev + 1))}
        >
          Proxima
        </button>
        <span className="pdf-page-indicator">
          Pagina {numPages ? pageNumber : 0} de {numPages || 0}
        </span>
      </div>

      <div className="pdf-viewer-canvas-container">
        {!error && (
          <canvas
            ref={canvasRef}
            className="pdf-canvas"
            aria-label={title ? `Preview de ${title}` : 'Preview de PDF'}
          />
        )}

        {(loading || rendering) && (
          <div className="pdf-loading-overlay">Carregando PDF...</div>
        )}

        {error && (
          <div className="pdf-error-state">
            <p>{error}</p>
            <button
              type="button"
              className="control-btn"
              onClick={() => setRetryToken((prev) => prev + 1)}
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PdfViewer;
